import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { currentActivityOption, EntityFormOption, InjectedOptions, optionId } from '../../../entity-form/entity-form.utils';
import {
  conceptPrefix,
  CREDIT_TERM_LABELS,
  CreditDialogData,
  creditTitle,
  displayDate,
  EMPTY_GUID,
  fromApiDate,
  roundMoney,
  sortByDateDesc,
  stripConceptPrefix,
  toApiDate,
} from '../../models/credit-shared.model';
import { CreditOperationsService } from '../../services/credit-operations.service';
import { CreditLookupsService, CreditProjectLookups } from '../../services/credit-lookups.service';
import { CreditFormLookupsFacade } from '../../services/credit-form-lookups.facade';

const TERM_OPTIONS: EntityFormOption[] = [
  { id: 1, display: CREDIT_TERM_LABELS[1] },
  { id: 2, display: CREDIT_TERM_LABELS[2] },
];

@Component({
  selector: 'app-credit-constitution-dialog',
  templateUrl: './credit-constitution-dialog.component.html',
  styleUrls: ['../../styles/credit-dialog.scss'],
  standalone: false,
})
export class CreditConstitutionDialogComponent implements OnInit {
  readonly termLabels = CREDIT_TERM_LABELS;
  readonly termOptions = [...TERM_OPTIONS];

  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  changed = false;
  private requestedEditDone = false;

  items: any[] = [];
  editing: any | null = null;

  activities: EntityFormOption[] = [];
  bankAccounts: EntityFormOption[] = [];
  projects: EntityFormOption[] = [];
  expenseProjectTypes: EntityFormOption[] = [];
  isProjectEnable = false;

  form: UntypedFormGroup;

  private readonly injected = new InjectedOptions();

  static open(dialog: MatDialog, data: CreditDialogData): Observable<boolean> {
    return dialog.open<CreditConstitutionDialogComponent, CreditDialogData, boolean>(CreditConstitutionDialogComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreditConstitutionDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditDialogData,
    private operations: CreditOperationsService,
    private lookups: CreditLookupsService,
    private formLookups: CreditFormLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      plazo: [null],
      fecha: [new Date()],
      concepto: [''],
      importe: [null],
      comisionApertura: [null],
      gastosBancarios: [null],
      activity: [null],
      bankAccount: [null],
      project: [null],
      expenseProjectType: [null],
    });
  }

  get isEmployee(): boolean {
    return this.data.kind === 'employee';
  }

  get title(): string {
    return 'Constitución del crédito';
  }

  get subtitle(): string {
    const parts = [creditTitle(this.data.kind, this.data.credit)];
    if (!this.isEmployee && this.termLabels[this.data.credit.duracion]) parts.push(this.termLabels[this.data.credit.duracion]);
    return parts.filter(Boolean).join(' · ');
  }

  get yearHint(): string {
    return this.isEmployee ? '' : 'Ejercicio ' + ActualSelected.yearSelected;
  }

  get total(): number {
    return roundMoney(this.items.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get missingPlazo(): boolean { return this.isEmployee && optionId(this.form.get('plazo').value) === null; }
  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean { return optionId(this.form.get('bankAccount').value) === null; }

  get invalidAmount(): boolean {
    const value = Number(this.form.get('importe').value);
    return this.isEmployee ? !value : !value || value <= 0;
  }

  get showProjects(): boolean {
    return !this.isEmployee && this.isProjectEnable && this.projects.length > 0;
  }

  get showExpenseProjectTypes(): boolean {
    return !this.isEmployee && this.isProjectEnable && this.expenseProjectTypes.length > 0;
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  ngOnInit(): void {
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  startEdit(item: any): void {
    this.editing = item;
    this.submitted = false;
    this.injected.clear();
    this.form.patchValue({
      plazo: this.injected.ensure(this.termOptions, item.plazo),
      fecha: fromApiDate(item.fecha),
      concepto: stripConceptPrefix(item.concepto, conceptPrefix(this.data.kind, this.data.credit)),
      importe: item.importe != null ? Number(item.importe) : null,
      comisionApertura: item.comisionApertura != null ? Number(item.comisionApertura) : null,
      gastosBancarios: item.gastosBancarios != null ? Number(item.gastosBancarios) : null,
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
      bankAccount: this.injected.ensure(this.bankAccounts, item.bankAccountsUid, item.bankAccountInfo),
      project: this.injected.ensure(this.projects, item.projectUid, item.projectName),
      expenseProjectType: this.injected.ensure(this.expenseProjectTypes, item.expenseProjectTypeUid, item.expenseProjectTypeName),
    }, { emitEvent: false });
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (this.missingPlazo || this.missingDate || this.missingConcept || this.invalidAmount || this.missingActivity || this.missingBankAccount) {
      return;
    }

    const isEdit = !!this.editing;
    const payload = this.buildPayload();
    this.saving = true;
    this.savingMessage = isEdit ? 'Actualizando constitución…' : 'Registrando constitución…';
    this.dialogRef.disableClose = true;

    this.operations.saveConstitution(this.data.kind, payload, isEdit)
      .then(ok => { if (ok !== true) throw new Error(); })
      .then(() => {
        this.changed = true;
        this.notify.ok(isEdit ? 'Constitución actualizada correctamente' : 'Constitución registrada correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.loadItems(false);
      })
      .catch(error => {
        const serverMessage = error && error.error && error.error.message;
        this.notify.error(serverMessage || 'No se pudo guardar la constitución del crédito');
      })
      .then(() => {
        this.saving = false;
        this.dialogRef.disableClose = false;
      });
  }

  remove(item: any): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar constitución',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará la constitución del crédito y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando constitución…',
      errorText: 'No se pudo eliminar la constitución',
      action: () => this.operations.deleteConstitution(this.data.kind, item.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Constitución eliminada correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.loadItems(false);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, item: any): string {
    return item.uid;
  }

  itemDate(item: any): string {
    return displayDate(item.fecha);
  }

  itemMeta(item: any): string {
    const parts: string[] = [];
    if (this.isEmployee && this.termLabels[item.plazo]) parts.push(this.termLabels[item.plazo]);
    parts.push(stripConceptPrefix(item.concepto, conceptPrefix(this.data.kind, this.data.credit)));
    if (Number(item.comisionApertura)) parts.push('Comisión ' + Utils.FormatMoney(item.comisionApertura) + ' €');
    if (Number(item.gastosBancarios)) parts.push('Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €');
    if (item.bankAccountInfo) parts.push(item.bankAccountInfo);
    return parts.filter(Boolean).join(' · ');
  }


  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.items.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    const projects: Promise<CreditProjectLookups> = this.isEmployee
      ? Promise.resolve({ enabled: false, projects: [], expenseProjectTypes: [] })
      : this.lookups.projects();

    Promise.all([
      this.operations.listConstitutions(this.data.kind, this.data.credit.uid),
      this.lookups.activities(),
      this.formLookups.bankAccounts(),
      projects,
    ])
      .then(([list, activities, bankAccounts, projectLookups]) => {
        this.activities = (activities || [])
          .filter(activity => activity && activity.uid && activity.uid !== EMPTY_GUID)
          .map(activity => ({ id: activity.uid, display: activity.name || '' }));
        this.bankAccounts = bankAccounts;
        this.isProjectEnable = projectLookups.enabled;
        this.projects = (projectLookups.projects || []).map(project => ({ id: project.id, display: project.display || '' }));
        this.expenseProjectTypes = (projectLookups.expenseProjectTypes || []).map(type => ({ id: type.id, display: type.display || '' }));
        this.items = sortByDateDesc(list, 'fecha');
        this.applySingleOptionDefaults();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private loadItems(initial: boolean): void {
    if (initial) this.ready = false;
    this.operations.listConstitutions(this.data.kind, this.data.credit.uid)
      .then(list => {
        this.items = sortByDateDesc(list, 'fecha');
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) this.loadError = true;
        else this.notify.error('No se pudieron recargar las constituciones');
      })
      .then(() => { this.ready = true; });
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;
    if (optionId(this.form.get('activity').value) === null) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (optionId(this.form.get('bankAccount').value) === null && this.bankAccounts.length === 1) {
      this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
    }
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const credit = this.data.credit;
    const payload = this.editing ? { ...this.editing } : this.operations.newConstitution(this.data.kind);
    payload[this.operations.parentKey(this.data.kind)] = credit.uid;
    payload.plazo = this.isEmployee ? Number(optionId(value.plazo)) : credit.duracion;
    payload.fecha = toApiDate(value.fecha);
    payload.concepto = value.concepto;
    payload.importe = Number(value.importe) || 0;
    payload.comisionApertura = Number(value.comisionApertura) || 0;
    payload.gastosBancarios = Number(value.gastosBancarios) || 0;
    payload.activitiesUid = optionId(value.activity);
    payload.bankAccountsUid = optionId(value.bankAccount);
    if (!this.isEmployee) {
      payload.projectUid = optionId(value.project);
      payload.expenseProjectTypeUid = optionId(value.expenseProjectType);
    }
    return payload;
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.form.reset({
      plazo: null,
      fecha: new Date(),
      concepto: '',
      importe: null,
      comisionApertura: null,
      gastosBancarios: null,
      activity: null,
      bankAccount: null,
      project: null,
      expenseProjectType: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private itemDetails(item: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: displayDate(item.fecha) });
    const concept = stripConceptPrefix(item.concepto, conceptPrefix(this.data.kind, this.data.credit));
    if (concept) details.push({ label: 'Concepto', value: concept });
    details.push({ label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' });
    return details;
  }
}
